import { useCallback, useState } from 'react';

import { makeHTTPProvider } from 'shared/infra/providers';
import { useTotemInfo, useUnit } from 'shared/presentation/contexts';

interface IVectisTermsConsentAggregate {
  consentiuTermosContrato: boolean;
  idCliente: number;
  email: string;
  foneCelular: string;
  whatsappValido: boolean;
  fluxoConsentimentoTermoContratoAtivo: boolean;
}

interface ITermsConsentAggregate {
  enabled: boolean;
  customer: {
    id: number;
    email: string;
    phone: string;
    validWhatsapp: boolean;
  };
}

export default function useTermsConfirmation(params?: {
  onError?: (error: unknown) => void;
}) {
  const [termsData, setTermsData] = useState<ITermsConsentAggregate | null>(
    null,
  );

  const { unit } = useUnit();
  const { language } = useTotemInfo();

  const shouldConsentToTerms = useCallback(
    async (paramsRequest: { id?: number; document?: string }) => {
      if (!unit) return false;

      try {
        const aggregate = await makeHTTPProvider({
          language,
        }).get<IVectisTermsConsentAggregate>(
          'clientes/consentimentoTermosContrato',
          {
            params: {
              idCliente: paramsRequest.id,
              cpf: paramsRequest.document,
              idUnidade: unit.id,
            },
          },
        );

        const enabled =
          aggregate.fluxoConsentimentoTermoContratoAtivo &&
          !aggregate.consentiuTermosContrato;

        if (enabled) {
          setTermsData(buildTermsData(aggregate));
        }

        return enabled;
      } catch (error) {
        params?.onError?.(error);
        throw error;
      }
    },
    [unit, language, params],
  );

  return {
    shouldConsentToTerms,
    termsData,
    resetData: () => setTermsData(null),
  };
}

function buildTermsData({
  consentiuTermosContrato,
  fluxoConsentimentoTermoContratoAtivo,
  email,
  foneCelular,
  idCliente,
  whatsappValido,
}: IVectisTermsConsentAggregate): ITermsConsentAggregate {
  return {
    enabled: fluxoConsentimentoTermoContratoAtivo && !consentiuTermosContrato,
    customer: {
      id: idCliente,
      email,
      phone: foneCelular,
      validWhatsapp: whatsappValido,
    },
  };
}
